import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ErrorState {
  code: string;
  message: string;
}
interface AppState {
  claims: Claim[];
  filteredClaims: Claim[];
  searchValue: string;
  filteredClaim: Claim;
  isClaimLoading: boolean;
  isNoDataFound: boolean;
  error: ErrorState | null;
  favoriteDomains: string[];
  generatedClaimsForClaim: { [claimText: string]: string[] };

  searchPrompt: string;
}

const initialState: AppState = {
  claims: [],
  filteredClaims: [],
  searchValue: '',
  filteredClaim: {} as Claim,
  isClaimLoading: false,
  isNoDataFound: false,
  favoriteDomains: [],
  generatedClaimsForClaim: {} as Record<string, string[]>,
  searchPrompt: '',
  error: null,
};

const claimsV2Slice = createSlice({
  name: 'claimsV2',
  initialState,
  reducers: {
    setClaims: (state: AppState, action: PayloadAction<Claim[]>) => {
      state.claims = action.payload;
    },
    addClaim: (state: AppState, action: PayloadAction<Claim>) => {
      state.claims = [...state.claims, action.payload];
    },
    updateClaim: (state: AppState, action: PayloadAction<Claim & { oldClaimText?: string }>) => {
      const matchText = action.payload.oldClaimText ?? action.payload.claim;
      state.claims = state.claims.map((claim) => {
        if (claim.claim === matchText) {
          return { ...claim, ...action.payload };
        }
        return claim;
      });
    },
    setSearchValue: (state: AppState, action: PayloadAction<string>) => {
      state.searchValue = action.payload;
    },
    setGeneratedClaims: (
      state: AppState,
      action: PayloadAction<{ claimText: string; relatedClaims: string[] }>
    ) => {
      state.generatedClaimsForClaim[action.payload.claimText] = action.payload.relatedClaims;
    },
    setSearchPrompt: (state, action: PayloadAction<string>) => {
      state.searchPrompt = action.payload;
    },
    setFilteredClaim: (state: AppState, action: PayloadAction<Claim>) => {
      state.filteredClaim = action.payload;
    },
    setFilteredClaims: (state: AppState, action: PayloadAction<Claim[]>) => {
      state.filteredClaims = action.payload;
      state.filteredClaims.map((claim) => {
        state.filteredClaim = claim;
      });
    },
    setIsClaimLoading: (state: AppState, action: PayloadAction<boolean>) => {
      state.isClaimLoading = action.payload;
    },
    setIsNoDataFound: (state: AppState, action: PayloadAction<boolean>) => {
      state.isNoDataFound = action.payload;
    },
    reset: (state) => ({
      ...initialState,
      favoriteDomains: state.favoriteDomains,
      searchPrompt: state.searchPrompt,
    }),
    resetOnLogout: () => initialState,
    resetOnSearch: (state) => ({
      ...initialState,
      searchValue: state.searchValue,
      favoriteDomains: state.favoriteDomains,
      searchPrompt: state.searchPrompt,
    }),
    setFavoriteDomains: (state: AppState, action: PayloadAction<string[]>) => {
      state.favoriteDomains = action.payload;
    },
    setError: (state: AppState, action: PayloadAction<ErrorState>) => {
      state.error = action.payload;
    },
    resetError: (state: AppState) => {
      state.error = null;
    },
  },
});

export const {
  setClaims,
  addClaim,
  updateClaim,
  setFilteredClaims,
  setSearchValue,
  setFilteredClaim,
  setIsClaimLoading,
  setIsNoDataFound,
  reset,
  resetOnLogout,
  resetOnSearch,
  setFavoriteDomains,
  setError,
  resetError,
  setGeneratedClaims,
  setSearchPrompt,
} = claimsV2Slice.actions;
export default claimsV2Slice.reducer;
export const getInitialState = () => initialState;
